import React, { useState } from "react"
import styled from "styled-components"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import axios from "axios"

// Custom Hooks
import { useLocale } from "../../hooks/useLocale"

// Styling
import colors from "../../styles/colors"
import mq from "../../styles/breakpoints"
import textStyles, { fontWeights } from "../../styles/textStyles"
import { fonts } from "../../styles/fonts"

// Components
import TextField from "./fields/TextField"
import TextArea from "./fields/TextArea"
import Button from "../atoms/Button"
import Icon from "../icons/Icon"
import Dropdown from "./fields/Dropdown"

// Types
import { ILabels } from "../../prismic-types/request-demo-page"

// Utils
import { generateDownloadUrl } from "../../utils/hashUrl"

export type IRequestVariant = "contact" | "download"
interface IProps {
  variant: IRequestVariant
  download?: string
  buttonLabel?: string
  onSuccess?: () => void
  labels?: ILabels
  complete: boolean
  setComplete: (value: boolean) => void
}

interface IFormData {
  name?: string
  email: string
  company: string
  employees: string
  message: string
}

const RequestDemoForm: React.FC<IProps> = ({
  variant,
  buttonLabel,
  download,
  onSuccess,
  labels,
  complete,
  setComplete,
}) => {
  const [providedEmail, setProvidedEmail] = useState("")
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const { isDutch } = useLocale()

  const validationSchemaContact = yup.object().shape({
    name: yup
      .string()
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
    email: yup
      .string()
      .email(
        isDutch
          ? "Dit is geen geldig email adres"
          : "This is not a valid email address"
      )
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
    company: yup
      .string()
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
    employees: yup
      .string()
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
    message: yup
      .string()
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
  })

  const validationSchemaDownload = yup.object().shape({
    email: yup
      .string()
      .email(
        isDutch
          ? "Dit is geen geldig email adres"
          : "This is not a valid email address"
      )
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
    company: yup
      .string()
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
    employees: yup
      .string()
      .required(isDutch ? "Dit veld is verplicht" : "This field is required"),
  })

  const { register, handleSubmit, errors } = useForm<IFormData>({
    resolver: yupResolver(
      variant === "contact" ? validationSchemaContact : validationSchemaDownload
    ),
  })

  const onSubmit = handleSubmit(
    async ({ name, email, company, employees, message }: IFormData) => {
      setLoading(true)
      setError(false)
      setComplete(false)
      setProvidedEmail(email)
      const request =
        variant === "download"
          ? `New download request: ${download}`
          : "New contact form request"
      await axios
        .post("/.netlify/functions/sendEmail", {
          formType: variant,
          hashUrl: generateDownloadUrl("/resources"),
          fileName: variant === "download" ? download : "",
          request,
          name: name || "-",
          email,
          company,
          employees,
          message: message || "-",
        })
        .then(() => {
          setLoading(false)
          setComplete(true)
          if (onSuccess !== undefined) {
            onSuccess()
          }
        })
        .catch(() => {
          setLoading(false)
          setError(true)
        })
    }
  )

  return (
    <Container>
      {loading && (
        <Loading>
          <Icon
            icon="loading"
            color={colors.blueGrey}
            width={120}
            height={120}
          />
        </Loading>
      )}
      <form onSubmit={onSubmit}>
        {!complete && (
          <>
            {variant === "contact" ? (
              <>
                {variant === "contact" && (
                  <TextArea
                    label={labels?.notes || ""} // @Asad Javed change this with message when you change the field name in prismic
                    name="message"
                    reference={register}
                  />
                )}
                <FieldContainer>
                  <TextField
                    label={labels?.name || ""}
                    name="name"
                    reference={register}
                    error={errors.name?.message}
                  />
                  <TextField
                    label={labels?.email || ""}
                    name="email"
                    reference={register}
                    error={errors.email?.message}
                  />
                </FieldContainer>
              </>
            ) : (
              <TextField
                label={labels?.email || ""}
                name="email"
                reference={register}
                error={errors.email?.message}
              />
            )}
            <FieldContainer>
              <TextField
                label={labels?.company || ""}
                name="company"
                reference={register}
                error={errors.company?.message}
              />
              <Dropdown
                label={labels?.employees || ""}
                name="employees"
                reference={register}
                error={errors.employees?.message}
                options={[
                  {
                    label: isDutch ? "Onder 10" : "Under 10",
                    value: "<10",
                  },
                  {
                    label: isDutch ? "Tussen 10 en 30" : "Between 10 and 30",
                    value: "10-30",
                  },
                  {
                    label: isDutch ? "Tussen 30 en 50" : "Between 30 and 50",
                    value: "30-50",
                  },
                  {
                    label: isDutch ? "Tussen 50 en 100" : "Between 50 and 100",
                    value: "50-100",
                  },
                  {
                    label: isDutch ? "Meer dan 100" : "Over 100",
                    value: ">100",
                  },
                ]}
              />
            </FieldContainer>
          </>
        )}
        {complete && (
          <MessageContainer variant={variant}>
            <MessageTitle>
              {variant === "download"
                ? isDutch
                  ? "You’ve got mail"
                  : "You’ve got mail"
                : isDutch
                ? "Bericht ontvangen"
                : "Message received"}
              !
            </MessageTitle>
            <Message>
              <p>
                {variant === "download"
                  ? isDutch
                    ? "Thanks for your interest in SUMM."
                    : "Thanks for your interest in SUMM."
                  : isDutch
                  ? "We nemen zo snel als we kunnen contact met je op. Meestal is dit binnen 1 dag."
                  : "We will contact you as soon as possible, which is usually within 1 business day."}{" "}
              </p>
              <p>
                {variant === "download"
                  ? isDutch
                    ? "We linken je door naar de download omgeving"
                    : `You will receive your files on ${providedEmail} shortly.`
                  : null}
              </p>
            </Message>
          </MessageContainer>
        )}
        {error && (
          <MessageContainer variant={variant}>
            {isDutch
              ? "Er ging iets mis, probeer het ajb op nieuw"
              : "Something went wrong... please try again later."}
          </MessageContainer>
        )}
        {!complete && (
          <StyledButton
            type="submit"
            disabled={loading}
            variant="primary"
            label={
              buttonLabel ||
              (isDutch ? "Bevestig en verzend" : "Confirm and send")
            }
          />
        )}
      </form>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  text-align: left;

  > form {
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`

const Loading = styled.div`
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
`

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${mq.from.S`
    flex-direction: row;
    justify-content: space-between;

    > * {
      width: calc(50% - 12px);
    }
  `}
`

const MessageContainer = styled.div<{ variant: IRequestVariant }>`
  padding: 24px;
  border-radius: 8px;
  text-align: center;
  background-color: ${(props) =>
    props.variant === "contact" ? colors.whiteGrey : "#FFFFFF"};
  color: ${colors.main["300"]};
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const MessageTitle = styled.h3`
  font-family: ${fonts.primary};
  font-size: 32px;
  line-height: 1.12;
  font-weight: ${fontWeights.bold};
  letter-spacing: -1%;
  color: ${colors.purpleLight};
  margin-bottom: 0px;
`

const Message = styled.div`
  display: flex;
  flex-direction: column;
  ${textStyles.body};
  letter-spacing: -1%;
  color: ${colors.main["300"]};
  margin-bottom: 0px;
  gap: 8px;

  > p {
    margin-bottom: 0px;
  }
`

const StyledButton = styled(Button)`
  margin-top: 16px;
  width: fit-content;
  align-self: center;
`

export default RequestDemoForm
